/**
 * Circuit Breaker Pattern Implementation
 * 
 * Prevents cascading failures by temporarily halting calls to failing services.
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Too many failures, requests fail fast
 * - HALF_OPEN: Testing if service recovered
 */

export type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

export interface CircuitBreakerConfig {
  failureThreshold: number; // Number of failures before opening
  timeout: number; // Time to wait before attempting recovery (ms)
  name?: string; // For logging
}

export class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private state: CircuitState = 'CLOSED';
  private successCount = 0;
  
  constructor(private config: CircuitBreakerConfig) {}

  /**
   * Execute a function with circuit breaker protection
   */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    // Check if circuit is OPEN
    if (this.state === 'OPEN') {
      const timeSinceLastFailure = Date.now() - this.lastFailureTime;
      
      if (timeSinceLastFailure > this.config.timeout) {
        // Transition to HALF_OPEN to test recovery
        this.state = 'HALF_OPEN';
        this.log('Transitioning to HALF_OPEN - testing recovery');
      } else {
        const waitTime = Math.ceil((this.config.timeout - timeSinceLastFailure) / 1000);
        throw new Error(`Circuit breaker is OPEN. Retry in ${waitTime}s`);
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  /**
   * Handle successful execution
   */
  private onSuccess() {
    if (this.state === 'HALF_OPEN') {
      this.successCount++;
      
      // Require 2 successes to transition from HALF_OPEN to CLOSED
      if (this.successCount >= 2) {
        this.log('Recovery confirmed - transitioning to CLOSED');
        this.reset();
      }
    } else {
      // Reset failure count on any success in CLOSED state
      this.failures = 0;
    }
  }

  /**
   * Handle failed execution
   */
  private onFailure() {
    this.failures++;
    this.lastFailureTime = Date.now();
    this.successCount = 0; // Reset success count on any failure
    
    if (this.failures >= this.config.failureThreshold) {
      if (this.state !== 'OPEN') {
        this.state = 'OPEN';
        this.log(`OPEN - ${this.failures} failures exceeded threshold of ${this.config.failureThreshold}`);
      }
    }
  }

  /**
   * Reset circuit breaker to initial state
   */
  private reset() {
    this.failures = 0;
    this.successCount = 0;
    this.state = 'CLOSED';
  }

  /**
   * Get current circuit breaker state
   */
  getState(): { state: CircuitState; failures: number } {
    return {
      state: this.state,
      failures: this.failures,
    };
  }

  /**
   * Manually reset the circuit breaker
   */
  manualReset() {
    this.log('Manual reset triggered');
    this.reset();
  }

  /**
   * Logging helper
   */
  private log(message: string) {
    const name = this.config.name ? `[${this.config.name}]` : '[CircuitBreaker]';
    console.warn(`${name} ${message}`);
  }
}
